import {
  type Control,
  type ControllerRenderProps,
  type DeepMap,
  type FieldError,
  type FieldValues,
  Controller,
} from 'react-hook-form';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';

interface DateInputProps {
  label: string;
  name: string;
  control: Control;
  required?: boolean;
  errors?: DeepMap<FieldValues, FieldError>;
  defaultValue?: any;
  minDate?: DateTime;
  maxDate?: DateTime;
  disabled?: boolean;
  onFocus?: () => void | null;
  onOpen?: () => void | null;
  onChange?: (updatedDate: DateTime | null) => void | null;
}

export default function DateInput({
  label,
  name,
  control,
  required = false,
  errors,
  defaultValue = undefined,
  minDate = undefined,
  maxDate = undefined,
  disabled = false,
  onFocus = undefined,
  onChange = undefined,
  ...otherProps
}: DateInputProps) {
  const { t } = useTranslation('common');
  const requiredFieldMessage = t('common.requiredField');
  const invalidDateMessage = t('common.invalidDate');

  const handleChange = (date: any, changeFn: (val: any) => void) => {
    changeFn(date);
  };

  const validateDate = (date: DateTime): boolean | string => {
    if (required && !date?.isValid) {
      return invalidDateMessage;
    }
    if (!required && date && !date.isValid) {
      return invalidDateMessage;
    }
    if (minDate && date && date < minDate) {
      const msg = t('common.dateTooEarly', {
        date: minDate.toLocaleString(DateTime.DATE_SHORT),
      });
      return msg;
    }
    if (maxDate && date && date > maxDate) {
      const msg = t('common.dateTooLate', {
        date: maxDate.toLocaleString(DateTime.DATE_SHORT),
      });
      return msg;
    }
    return true;
  };

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      rules={{
        required: required && requiredFieldMessage,
        validate: validateDate,
      }}
      render={({ field }: { field: ControllerRenderProps }) => {
        return (
          <DatePicker
            label={label}
            value={field.value}
            minDate={minDate}
            maxDate={maxDate}
            disabled={disabled}
            slotProps={{
              textField: {
                variant: 'filled',
                required,
                error: errors ? !!errors[name] : false,
                helperText: errors?.[name]?.message ?? null,
                onFocus: () => {
                  onFocus && onFocus();
                },
              },
            }}
            onChange={(date) => {
              handleChange(date, field.onChange);
              onChange && onChange(date);
            }}
            {...otherProps}
          />
        );
      }}
    />
  );
}
